'use es6';

import http from 'hub-http-rxjs/clients/apiClient';
import hubHttp from 'hub-http/clients/apiClient';
import { map } from 'rxjs/operators/map';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { of } from 'rxjs/observable/of';
import { _throw as throwError } from 'rxjs/observable/throw';
import { getChangeAudienceAccessPayload } from 'ContentUtils/helpers/AudienceAccessHelpers';
import PortalIdParser from 'PortalIdParser';
import { ContentTypes, ContentTypeToSubcategory, PageState } from '../constants/base';
import Content from '../models/Content';
import getEarlyRequesterResultPromise from '../earlyRequester/getEarlyRequesterResultPromise';
const PAGES_BASE_PATH = 'cospages/v2';
const getPagesBasePath = requestType => requestType === 'GET' ? `${PAGES_BASE_PATH}/dashboard` : PAGES_BASE_PATH;
const getUri = (contentType, opts = {}) => {
  if (contentType === ContentTypes.BLOG_POST) {
    return 'blogs/v3/blog-posts';
  } else if (contentType === ContentTypes.LANDING_PAGE) {
    return `${getPagesBasePath(opts.requestType)}/landing-pages`;
  } else if (contentType === ContentTypes.SITE_PAGE) {
    return `${getPagesBasePath(opts.requestType)}/site-pages`;
  } else if (contentType === ContentTypes.BLOG_LISTING_PAGE) {
    return 'blogs/v3/blog-listing-pages';
  }
  return '';
};
const getUriUsingSharedPagesApi = contentType => {
  switch (contentType) {
    case ContentTypes.BLOG_POST:
      return 'blogs/v3/blog-posts';
    case ContentTypes.BLOG_LISTING_PAGE:
      return 'blogs/v3/blog-listing-pages';
    case ContentTypes.LANDING_PAGE:
    case ContentTypes.SITE_PAGE:
      return 'cospages/v2/pages';
    default:
      return '';
  }
};
const generateUrl = (contentType, id) => `${getUri(contentType)}/${id}`;
export default {
  archiveAndUnpublishContent(contentType, id) {
    const query = {
      failIfNotAllSucceed: true,
      id
    };
    return http.put(`${getUri(contentType)}/archive-and-unpublish`, {
      query
    });
  },
  archiveContent(contentType, id) {
    const data = {
      archived: true
    };
    return http.put(generateUrl(contentType, id), {
      data
    }).pipe(map(Content.fromJS));
  },
  attachToLanguageMaster(contentType, id, language, translatedFromId) {
    const options = {
      data: {
        language,
        translatedFromId
      },
      timeout: 30000
    };
    return http.put(`${generateUrl(contentType, id)}/attach-to-lang-master`, options).pipe(map(Content.fromJS));
  },
  changeAudienceAccess(contentType, id, audienceAccessType, password, lists) {
    const listsToInclude = lists ? lists.toArray() : [];
    const data = getChangeAudienceAccessPayload(audienceAccessType, password, listsToInclude);
    return http.put(`${getUri(contentType)}/restrict-access`, {
      data,
      query: {
        id
      }
    });
  },
  // Memberships shared library content access version, see https://git.hubteam.com/HubSpot/ContentMembership/issues/907
  changeContentAccess(contentType, contentId, audienceAccessOptions) {
    return http.put(`${getUri(contentType)}/restrict-access`, {
      data: audienceAccessOptions,
      query: {
        id: contentId
      }
    });
  },
  cloneContent(contentType, id, name) {
    const data = {
      name,
      state: PageState.DRAFT
    };
    const query = {
      _buffer: true
    };
    return http.post(`${generateUrl(contentType, id)}/clone`, {
      data,
      query
    });
  },
  convertContent(contentType, id, newContentType) {
    const data = ContentTypeToSubcategory[newContentType];
    return http.post(`${generateUrl(contentType, id)}/subcategory-move`, {
      data
    });
  },
  createTranslationVariation(contentType, id, language, masterLanguage, name, contentGroupId) {
    const data = {
      language,
      name
    };
    if (contentType === ContentTypes.BLOG_POST) {
      data.contentGroupId = contentGroupId;
    } else {
      data.masterLanguage = masterLanguage;
    }
    const options = {
      data,
      query: {
        _buffer: true
      },
      timeout: 30000
    };
    return http.post(`${generateUrl(contentType, id)}/create-language-variation`, options);
  },
  deleteContent(contentType, id) {
    return http.delete(generateUrl(contentType, id));
  },
  fetchAllContentByAnalyticsId({
    analyticsPageId,
    contentType
  }) {
    const query = {
      analyticsPageId__eq: analyticsPageId
    };
    const opts = {
      requestType: 'GET'
    };
    return hubHttp.get(getUri(contentType, opts), {
      query
    }).then(response => response.objects.map(o => Content.fromJS(o)));
  },
  fetchContent({
    analyticsPageId,
    contentType,
    contentPageId
  }) {
    if (analyticsPageId) {
      return this.fetchContentByAnalyticsId({
        analyticsPageId,
        contentType
      });
    }
    const opts = {
      requestType: 'GET'
    };
    const request = getEarlyRequesterResultPromise({
      name: `content-${contentPageId}`,
      retryRequester: () => hubHttp.get(`${getUri(contentType, opts)}/${contentPageId}`, {
        query: {
          includeDeleted: true
        }
      })
    });
    return request.pipe(map(Content.fromJS));
  },
  fetchContentByAnalyticsId({
    analyticsPageId,
    contentType
  }) {
    const query = {
      analyticsPageId__eq: analyticsPageId,
      includeDeleted: true,
      limit: 1
    };
    const opts = {
      requestType: 'GET'
    };
    return http.get(getUri(contentType, opts), {
      query
    }).pipe(mergeMap(response => response.objects.length === 1 ? of(Content.fromJS(response.objects[0])) : throwError({
      error: 'no content',
      status: 404
    })));
  },
  fetchContentMultiLangUsage(contentType, id) {
    return hubHttp.get(`${getUriUsingSharedPagesApi(contentType)}/${id}/multilang-usage`);
  },
  fetchContentUsage(contentType, shouldFetchFromSharedPageLimitApi) {
    if (shouldFetchFromSharedPageLimitApi) {
      return getEarlyRequesterResultPromise({
        name: 'content-usage',
        retryRequester: () => hubHttp.get('cospages/v2/pages/usage')
      });
    }
    return getEarlyRequesterResultPromise({
      name: 'content-usage',
      retryRequester: () => hubHttp.get(`${getUri(contentType)}/${PortalIdParser.get()}/usage`)
    });
  },
  publishContent(contentType, id) {
    const data = {
      action: 'schedule-publish'
    };
    return http.post(`${generateUrl(contentType, id)}/publish-action`, {
      data
    });
  },
  removeFromLanguageGroup(contentType, id) {
    return http.put(`${generateUrl(contentType, id)}/detach-from-lang-group`);
  },
  unarchiveContent(contentType, id) {
    const data = {
      archived: false
    };
    return http.put(generateUrl(contentType, id), {
      data
    }).pipe(map(Content.fromJS));
  },
  unpublishContent(contentType, id) {
    const data = {
      action: 'cancel-publish'
    };
    return http.post(`${generateUrl(contentType, id)}/publish-action`, {
      data
    });
  },
  updateTitle(contentType, id, title) {
    return http.put(generateUrl(contentType, id), {
      data: {
        name: title
      }
    }).pipe(map(Content.fromJS));
  }
};